import DocumentViewerModal from "components/DocumentViewer";
import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import { useTranslation } from "react-i18next";
import TabNav from "components/TabNav";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateLegislativeDrafting } from "services/legislativeService";
import { updateRegisterDirective } from "services/registerDirectivesService";
import { updateSectorDirective } from "services/sectorDirectives";
import ShowUI, { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { LoadingIcon } from "utils/constant";
import { SECTOR_DIRECTIVES_REQUEST_STATUS, LEGAL_RESEARCH_REGISTRAR_TABS, LEGAL_RESEARCH_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { formatDate, formatDay } from "utils/helpers";
import LegislativeDraftDetail from "./fragments/leftPanel";
import LegislativeDraftRightPanel from "./fragments/rightPanel";
import { appendOfficeStamp } from "services/stampService";

export default function RegistrarLegislativeDraft({ request }: { request: any }) {
    const { t } = useTranslation();
    const contextData = useAuth();
    const [activeTab, setActiveTab] = useState("Research Finding");
    const [letterRefNum, setLetterRefNum] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [completionFile, setCompletionFile] = useState<File | null>(null);
    const [uploadingCompletion, setUploadingCompletion] = useState(false);
    const { showToast } = useToast();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLetterRefNum(e.target.value);
    };

    const [formState, setFormState] = useState({ ...request });

    const handleSave = async () => {
        const data = new FormData();
        data.append(
            "status",
            SECTOR_DIRECTIVES_REQUEST_STATUS.letter_refnum_submitted.value
        );
        data.append(
            "forward_status",
            SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value
        );
        data.append("registry_number", letterRefNum);

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);
            setFormState({ ...response });
            showToast("Legal research responded back successfully!", "success");
        } catch (error) { 
            console.error("Error submitting request:", error);
            showToast("Failed to update legal research request.", "error");
        } finally {
            setSubmitting(false);
            setLetterRefNum("");
        }
    };

    const handleUploadCompletionLetter = async () => {
        if (!completionFile) {
            showToast("Please select a file to upload.", "error");
            return;
        }
        setUploadingCompletion(true);
        const data = new FormData();
        data.append("research_completion_letter_files", completionFile);
        // Office stamp from HRMS: the backend stamps the PDF before storing it.
        await appendOfficeStamp(data);
        data.append("document_type", "dir_reg_research_completion_letter");

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);
            setFormState({ ...response });
            showToast("Research completion letter uploaded successfully!", "success");
            setCompletionFile(null); 
        } catch (error) {
            console.error("Error uploading completion letter:", error);
            showToast("Failed to upload research completion letter.", "error");
        } finally {
            setUploadingCompletion(false);
        }
    };

    // Get only the latest dir_reg_written_comments document
    const latestWrittenCommentsDoc = formState?.documents
        ?.filter((document: any) => document.document_type === "dir_reg_written_comments")
        ?.[0];

    // Check if completion letter already uploaded
    const completionLetterDoc = formState?.documents?.find(
        (document: any) => document.document_type === "dir_reg_research_completion_letter"
    );

    useEffect(() => { console.log(formState); }, [formState]);

    return (
        <div className="flex h-full bg-gray-100 p-2 gap-4">
            {/* Left Panel */}
            <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
                <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between pb-4 mb-3">
                        <div className="flex items-center space-x-2">
                            <h1 className="text-2xl font-semibold text-gray-800">
                                Request for Directive Registeration
                            </h1>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-scroll scrollbar-hide">
                        {/* Draft Summary - show only latest written comments file */}
                        <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                            <div className="-mx-4 -mt-4 mb-4 bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-4 py-3">
                                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                                <p className="text-white font-semibold text-base">{t("DraftSummary")}</p>
                            </div>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                            <p className="font-regular text-base text-[#606060]">{formState?.summary_of_comments}</p>
                            {latestWrittenCommentsDoc && (
                                <DocumentViewerModal
                                    documents={[latestWrittenCommentsDoc]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`} 
                                />
                            )}
                        </div>


                        {/* Documents */}
                        <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
                            {formState?.documents?.find((document: any) => document.document_type === "directive_reg") &&
                                <>
                                    {formState?.registry_number && 
                                        <p className="text-[#432101AB] font-normal text-base">Register Number: {formState.registry_number}</p>
                                    }
                                    {formState?.documents?.find((document: any) => document.document_type === "directive_reg") &&
                                        <DocumentViewerModal
                                            documents={formState?.documents.filter((document: any) => document.document_type === "directive_reg")}
                                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                        />
                                    }
                                </>
                            }
                            {formState?.documents?.find((document: any) => document.document_type === "reg_directive_request_letter") &&
                                <DocumentViewerModal
                                    documents={formState?.documents.filter((document: any) => document.document_type === "reg_directive_request_letter")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                />
                            }
                            <div className="mb-8">
                                <p className="text-[#432101AB] font-normal text-base">{t("ExplanatoryNote")}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.explanatory_notes}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Panel */}
            <div className="w-[40%] flex flex-col gap-4 overflow-y-scroll scrollbar-hide">

                {/* Letter Reference Number Card */}
                <div className="bg-white rounded-lg shadow-md p-6 overflow-hidden">
                    <div className="-mx-6 -mt-6 mb-4 bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-6 py-3">
                        <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                        <p className="font-semibold text-white">{t("LetterReferenceNumber")}</p>
                    </div>
                    {formState?.request_letter_reference_number ? (
                        <p className="text-[#432101AB] font-normal text-base"> 
                            Register Letter Number: {formState?.request_letter_reference_number}
                        </p>
                    ) : (
                        <p className="text-gray-400 text-sm">No letter reference number yet.</p>
                    )}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="mt-4">
                            <DynamicFormInput
                                inputs={[
                                    {
                                        value: letterRefNum,
                                        type: "text",
                                        label: "Letter Reference Number",
                                        name: "letter_reference_number",
                                        onChange: handleChange,
                                            placeholder: t("Eg12345"),
                                    },
                                ]}
                            />
                            <button
                                onClick={handleSave}
                                className="bg-[#0A1D39] hover:bg-[#13294d] mt-4 text-white font-bold py-3 px-8 rounded-md w-full"
                            >
                                {submitting ? (
                                    <div className="flex items-center justify-center">
                                            {LoadingIcon} {t("Submitting")}...
                                    </div>
                                ) : (
                                        t("Save")
                                )}
                            </button>
                        </div>
                    </ShowUI>

                    {/* Completion Letter section */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="pb-2 border-b border-gray-200 mb-4">
                        <p className="font-semibold text-gray-800">{t("CompletionLetter")}</p>
                    </div>

                    {completionLetterDoc ? (
                        /* Show uploaded file, hide form */
                        <div>
                            <p className="text-sm text-green-600 mb-3 flex items-center gap-1"> 
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"> 
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Completion letter uploaded
                            </p>
                            <DocumentViewerModal
                                documents={[completionLetterDoc]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        </div>
                    ) : (
                        /* Show upload form */
                        <div>
                            <DynamicFormInput
                                inputs={[
                                    {
                                        type: "file",
                                        label: t("CompletionLetter") + "*",
                                        name: "research_completion_letter_files",
                                        onChange: (file: any) => setCompletionFile(file),
                                    },
                                ]}
                            />
                            <button
                                onClick={handleUploadCompletionLetter}
                                disabled={uploadingCompletion || !completionFile}
                                className="bg-[#0A1D39] hover:bg-[#13294d] disabled:bg-gray-400 mt-4 text-white font-bold py-3 px-8 rounded-md w-full"
                            >
                                {uploadingCompletion ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Uploading")}...
                                    </div>
                                ) : (
                                    t("UploadCompletionLetter")
                                )}
                            </button>
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </div>
    );
}
